import { inject, Injectable } from '@angular/core';
import {
  DepartmentHead,
  SOFTLINE_API_PMS_ROSTER_CONTENT,
  SOFTLINE_API_PMS_ROSTER_DEPARTMENT_HEADS,
  SOFTLINE_API_PMS_ROSTER_STRUCTURE,
  Structure,
} from '@softapps/pms/roster-core';
import { ApiDataAccess, toIsoDate } from '@softline/core';
import { MessageBarService, modalFn, translator } from '@softline/ui-core';
import { EventFormComponent } from '../components/event-form/event-form.component';

@Injectable({ providedIn: 'root' })
export class SoftlineRosterGlobalCreateService {
  private readonly dataAccess = inject(ApiDataAccess);
  private readonly messageBarService = inject(MessageBarService);
  private readonly t = translator();
  private readonly modal = modalFn();

  async quickCreate(date: Date): Promise<void> {
    const today = new Date().toISOString().split('T')[0];

    const departmentHeads = await this.dataAccess.get<DepartmentHead[]>({
      path: SOFTLINE_API_PMS_ROSTER_DEPARTMENT_HEADS,
      queryParams: { cutoffdate: today },
    });

    if (!departmentHeads?.length) {
      this.messageBarService.error(this.t('#PMS_ROSTER.NO_DEPARTMENT_HEADS')());
      return;
    }

    const structures = await Promise.all(
      departmentHeads.map((dh) =>
        this.dataAccess
          .get<Structure>({
            path: SOFTLINE_API_PMS_ROSTER_STRUCTURE,
            pathParams: { employeeId: dh.employee.id },
            queryParams: { begindate: today, enddate: today },
          })
          .catch(() => null)
      )
    );

    const seen = new Set<number | string>();
    const resources = structures
      .filter(Boolean)
      .flatMap((s) => s?.departements ?? [])
      .flatMap((d) => d.employees)
      .filter((e) => {
        if (seen.has(e.id)) return false;
        seen.add(e.id);
        return true;
      })
      .map((e) => ({ id: e.id, name: e.name }));

    const eventData = await this.modal({
      title: this.t('#PMS_ROSTER.CREATE_ENTRY_TITLE')(),
      subtitle: this.t('#PMS_ROSTER.CREATE_ENTRY_SUBTITLE')(),
      icon: 'fa-regular fa-calendar-plus',
      separateContentSection: true,
      surfaceControls: { minimize: true, dock: true },
      component: EventFormComponent,
      data: {
        eventType: 'Planeintrag',
        date: toIsoDate(date),
        resource: [],
        resources,
      },
    });

    if (!eventData || eventData === 'DISMISSED' || eventData.eventType !== 'Planeintrag')
      return;

    const startString = eventData.beginDay + 'T' + eventData.beginTime;
    const endString = eventData.endDay + 'T' + eventData.endTime;
    const additionaldetail = [
      {
        tabname: eventData.ticketId ? 'TICKET' : 'OBJECT',
        tabid: eventData.ticketId ? eventData.ticketId : eventData.objectId,
        name: null,
        title: null,
        comment: null,
        state: 'UNDEFINED',
        key: null,
        id: null,
      },
    ];

    const payload = [
      {
        resource: [Number(eventData.employeeId)],
        date: startString,
        allDay: false,
        start: startString,
        end: endString,
        title: '',
        additionalData: {
          state: 'UNDEFINED',
          pauseHours: '00:30:00',
          plannedHours: '23:59:59',
          targetHours: '23:59:59',
          department: { id: 23, key: '31', name: 'Web', costCenters: null },
          employees: [
            {
              employee: { id: Number(eventData.employeeId) },
              department: { id: 23, key: '31', name: 'Web', costCenters: null },
            },
          ],
          begintime: startString,
          endtime: endString,
          type: 'PLAN',
          additionaldetail,
        },
      },
    ];

    try {
      await this.dataAccess.create({ path: SOFTLINE_API_PMS_ROSTER_CONTENT }, payload);
      this.messageBarService.success(this.t('#PMS_ROSTER.ENTRY_CREATED')());
    } catch {
      this.messageBarService.error(this.t('#PMS_ROSTER.ENTRY_CREATE_ERROR')());
    }
  }
}
